import { adminApi } from "../../lib/axios";

export type OnboardingPaymentStatus =
  | "PENDING"
  | "LINK_OPENED"
  | "PROCESSING"
  | "PAID"
  | "FAILED"
  | "CANCELLED"
  | "REFUNDED";

export interface OnboardingPaymentAdmin {
  id: string;
  email: string;
  name: string | null;
  token: string;
  url: string;
  amountCents: number;
  currency: string;
  status: OnboardingPaymentStatus;
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null;
  stripeCustomerId: string | null;
  amountPaidCents: number | null;
  receiptUrl: string | null;
  linkOpenedAt: string | null;
  paidAt: string | null;
  usedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  lastError: string | null;
  linkEmailSentAt: string | null;
  confirmationEmailSentAt: string | null;
  trialRevokedAt: string | null;
  createdByAdminId: string;
  createdAt: string;
  updatedAt: string;
  createdByAdmin?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  trialOtp?: {
    code: string;
    trialDays: number;
    isActive: boolean;
  } | null;
  events?: OnboardingPaymentEvent[];
}

export interface OnboardingPaymentEvent {
  id: string;
  paymentId: string | null;
  stripeEventId: string;
  type: string;
  status: string;
  signatureVerified: boolean;
  payload: unknown;
  error: string | null;
  receivedAt: string;
  processedAt: string | null;
}

export interface OnboardingPaymentListResponse {
  items: OnboardingPaymentAdmin[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateOnboardingPaymentPayload {
  email: string;
  name?: string;
}

export const onboardingAdminService = {
  async create(
    payload: CreateOnboardingPaymentPayload
  ): Promise<OnboardingPaymentAdmin> {
    const response = await adminApi.post<OnboardingPaymentAdmin>(
      "/onboarding-payment",
      payload
    );
    return response.data;
  },

  async list(params: {
    page?: number;
    limit?: number;
    status?: OnboardingPaymentStatus | "";
    search?: string;
  }): Promise<OnboardingPaymentListResponse> {
    const response = await adminApi.get<OnboardingPaymentListResponse>(
      "/onboarding-payment",
      {
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status || undefined,
          search: params.search || undefined,
        },
      }
    );
    return response.data;
  },

  async getOne(id: string): Promise<OnboardingPaymentAdmin> {
    const response = await adminApi.get<OnboardingPaymentAdmin>(
      `/onboarding-payment/${id}`
    );
    return response.data;
  },

  async resend(id: string): Promise<OnboardingPaymentAdmin> {
    const response = await adminApi.post<OnboardingPaymentAdmin>(
      `/onboarding-payment/${id}/resend`
    );
    return response.data;
  },

  async revoke(id: string): Promise<OnboardingPaymentAdmin> {
    const response = await adminApi.post<OnboardingPaymentAdmin>(
      `/onboarding-payment/${id}/revoke`
    );
    return response.data;
  },

  async revokeTrial(id: string): Promise<OnboardingPaymentAdmin> {
    const response = await adminApi.post<OnboardingPaymentAdmin>(
      `/onboarding-payment/${id}/revoke-trial`
    );
    return response.data;
  },
};
